import type {
  BusinessProduct,
  EnabledProducts,
  PayrollProductFeature,
  ProductFeatureCatalogue,
  ProductSettingsNode,
} from "./business";

// Mirror known access paths from the API's Business::PRODUCT_FEATURE_DEFAULTS.
// Ignore newer saved settings until Web supports them, without granting access.
const PRODUCT_FEATURES: Record<BusinessProduct, ProductFeatureCatalogue> = {
  accounts_payable: { receipts: {}, measure_usage: {} },
  payroll: {
    paypacks: { pack_chat_ai: {}, texts: {} },
    roster: {},
    pushpay: {},
    pay_plans: {},
    edit_pay_rules: {},
  },
  profitability: {},
  referrals: {},
};

const isKnownProduct = (product: string): product is BusinessProduct =>
  Object.prototype.hasOwnProperty.call(PRODUCT_FEATURES, product);

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isValidProductNode = (
  value: unknown,
  catalogue: ProductFeatureCatalogue,
): value is ProductSettingsNode => {
  if (!isObject(value) || typeof value.enabled !== "boolean") return false;
  if (!("features" in value)) return true;

  return (
    isObject(value.features) &&
    Object.entries(value.features).every(
      ([feature, child]) =>
        !Object.prototype.hasOwnProperty.call(catalogue, feature) ||
        typeof child === "boolean" ||
        isValidProductNode(child, catalogue[feature]),
    )
  );
};

// One malformed known entry still invalidates the selection object.
export const isValidEnabledProducts = (
  value: unknown,
): value is EnabledProducts =>
  isObject(value) &&
  Object.entries(value).every(
    ([product, settings]) =>
      !isKnownProduct(product) ||
      isValidProductNode(settings, PRODUCT_FEATURES[product]),
  );

export const isProductEnabled = (settings: unknown, product: BusinessProduct) =>
  isKnownProduct(product) &&
  isValidEnabledProducts(settings) &&
  settings[product]?.enabled === true;

export const isProductFeatureEnabled = (
  settings: unknown,
  product: BusinessProduct,
  ...path: string[]
) => {
  if (
    !path.length ||
    !isKnownProduct(product) ||
    !isValidEnabledProducts(settings)
  )
    return false;

  let catalogue = PRODUCT_FEATURES[product];
  let node: boolean | ProductSettingsNode | undefined = settings[product];
  if (node?.enabled !== true) return false;

  for (const feature of path) {
    if (!Object.prototype.hasOwnProperty.call(catalogue, feature)) return false;
    catalogue = catalogue[feature];
    if (!node || typeof node === "boolean") return false;
    node = node.features?.[feature];
    if (node !== true && (!node || node.enabled !== true)) return false;
  }
  return true;
};

export const isPayrollFeatureEnabled = (
  settings: unknown,
  feature: PayrollProductFeature,
) => isProductFeatureEnabled(settings, "payroll", feature);
